import React from 'react';
import { DesignTokens, GivelifyButtonProps, GivelifyLabel } from '@givelify/ui';
import { Stack } from '@mui/material';
import DarkGivelifyButton from '../../../../assets/online-giving/dark-givelify-button.svg';
import LightGivelifyButton from '../../../../assets/online-giving/light-givelify-button.svg';
import PrimaryGivelifyButton from '../../../../assets/online-giving/primary-givelify-button.svg';
import CopyGivingLink from '../../../../components/CopyGivingLink';
import withDownloadable, {
    WithDownloadableProps,
} from '../../../../components/withDownloadable';
import { GiveNowButtonType, GiveNowButtonTypes } from '../../../../types';
import { GIVENOW_BUTTON_URLS } from '../../../../types/const';
import { useTranslations } from '../../../../utils/translations';
import {
    Content,
    DownloadHelp,
    MainBox,
    NumberedLine,
    PreviewBox,
    PreviewImage,
    RadioBox,
    RadioButton,
} from './style';

const DownloadableButton = withDownloadable<
    GivelifyButtonProps & WithDownloadableProps
>(
    DownloadHelp as React.ComponentType<
        GivelifyButtonProps & WithDownloadableProps
    >,
);

export const InstructModalContent: React.FCC<{ givingLink: string }> = ({
    givingLink,
}) => {
    const [selectedButtonType, setSelectedButtonType] =
        React.useState<GiveNowButtonType>('lightGive');

    const { instructModal: im } = useTranslations(
        'onlineGiving.givelifyButton',
        {
            instructModal: [
                'title',
                'desc',
                'downloadButton',
                'goToBuilder',
                'addLink',
                'download',
                'preview',
                ...GiveNowButtonTypes,
            ],
        },
    );

    return (
        <Content data-testid="instruct-modal-content">
            <GivelifyLabel text={im.title} variant="heading2S" />
            <MainBox>
                <GivelifyLabel
                    paddingBottom={3}
                    paddingTop={3}
                    text={im.desc}
                    variant="body1B"
                />
                {/* 1. Download image button */}
                <div>
                    <div>
                        <NumberedLine color="primary-blue" variant="body1B">
                            1.
                            <GivelifyLabel
                                text={im.downloadButton}
                                variant="body1"
                            />
                        </NumberedLine>
                    </div>
                    <RadioBox>
                        {GiveNowButtonTypes.map((type) => (
                            <RadioButton
                                key={type}
                                aria-label={type}
                                checked={selectedButtonType === type}
                                color="primary"
                                data-testid={`${type}-radio`}
                                label={im[type]}
                                name={type}
                                onChange={(t) =>
                                    t ? setSelectedButtonType(type) : {}
                                }
                                width="auto"
                            />
                        ))}
                    </RadioBox>
                    <GivelifyLabel
                        color={DesignTokens.color.grey600}
                        text={im.preview}
                        variant="body2"
                    />
                    <PreviewBox>
                        <PreviewImage
                            alt={selectedButtonType}
                            data-testid="button-preview-image"
                            src={
                                selectedButtonType === 'primaryGive'
                                    ? PrimaryGivelifyButton
                                    : selectedButtonType === 'lightGive'
                                    ? LightGivelifyButton
                                    : DarkGivelifyButton
                            }
                        />
                    </PreviewBox>

                    <DownloadableButton
                        filename={`${selectedButtonType}-button`}
                        fileurl={GIVENOW_BUTTON_URLS[selectedButtonType]}
                        text={im.download}
                        variant="primary"
                    />
                </div>
                {/* 2. Go to website builder */}
                <Stack marginBottom={2}>
                    <NumberedLine color="primary-blue" variant="body1B">
                        2.
                        <GivelifyLabel text={im.goToBuilder} variant="body1" />
                    </NumberedLine>
                </Stack>
                {/* 3. Add organization giving link */}
                <div>
                    <Stack marginBottom={2}>
                        <NumberedLine color="primary-blue" variant="body1B">
                            3.
                            <GivelifyLabel text={im.addLink} variant="body1" />
                        </NumberedLine>
                    </Stack>
                    <CopyGivingLink givingLink={givingLink} />
                </div>
            </MainBox>
        </Content>
    );
};
